import { useAuthStore } from '@app/shared/store/auth.store';
import { useBranchStore } from '@app/shared/store/branch.store';

/**
 * Single source of truth for "is the app allowed to fire
 * business-scoped requests right now?" — combines auth identity,
 * business membership, and branch context readiness. Every hook
 * whose query includes `branchOfficeId` (either as a key part OR
 * via the axios auto-inject) must gate its `useQuery` on this
 * flag or it will fire during the identity-swap window with a
 * stale/empty branch and refire when the store settles.
 *
 * The check is intentionally strict — the failure mode of a false
 * negative (query stays idle 20ms longer than necessary) is
 * imperceptible; the failure mode of a false positive is the same
 * request storm that produced this hook.
 *
 * Ready when ALL of:
 *   1. `isAuthenticated` — token + user hydrated
 *   2. `user.businessId` present — auth resolved to a real tenant
 *   3. `branchOffices` list belongs to the CURRENT businessId
 *      (cross-tenant guard mirrored from api.service.ts's
 *      auto-inject block and branch.store.ts's
 *      `branchesMatchCurrentTenant` predicate).
 *   4. `selectedBranchOfficeId` is set AND either 'all' OR sits
 *      inside the current list.
 *
 * Callers that need to know why the context is NOT ready (e.g.
 * <AuthGuard> rendering a specific fallback) should use
 * `useBusinessContextReadyReason` below.
 */
export function useBusinessContextReady(): boolean {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const currentBusinessId = useAuthStore(
    (s) => s.user?.business?.id ?? s.user?.businessId ?? ''
  );
  const selectedBranchOfficeId = useBranchStore(
    (s) => s.selectedBranchOfficeId
  );
  const branchOffices = useBranchStore((s) => s.branchOffices);

  if (!isAuthenticated) return false;
  if (!currentBusinessId) return false;
  if (!selectedBranchOfficeId) return false;

  // Non-admin users (cashier / manager) never fetch the branch list
  // in handleAuthSuccess — they get their pinned `user.branchId`
  // written directly to `selectedBranchOfficeId` and no list. Empty
  // branchOffices for that path is EXPECTED and must be treated as
  // ready. Admin / super_admin always populate the list, so a
  // populated list must cross-tenant-check to catch impersonation
  // leaks.
  if (branchOffices.length === 0) return true;

  const branchesMatchTenant = branchOffices.every(
    (b) => !!b.businessId && b.businessId === currentBusinessId
  );
  if (!branchesMatchTenant) return false;

  if (
    selectedBranchOfficeId !== 'all' &&
    !branchOffices.some((b) => b.id === selectedBranchOfficeId)
  ) {
    return false;
  }

  return true;
}

/**
 * Same predicate as `useBusinessContextReady`, but returns a
 * discriminant so consumers can render specific loading states or
 * error surfaces. Consumers that only need the boolean should keep
 * using `useBusinessContextReady` — passing the discriminant to a
 * `useQuery` `enabled` prop would defeat the point.
 */
export type BusinessContextReadyReason =
  | 'ready'
  | 'unauthenticated'
  | 'missing-business-id'
  | 'branch-not-selected'
  | 'branch-tenant-mismatch'
  | 'branch-not-in-list';

export function useBusinessContextReadyReason(): BusinessContextReadyReason {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const currentBusinessId = useAuthStore(
    (s) => s.user?.business?.id ?? s.user?.businessId ?? ''
  );
  const selectedBranchOfficeId = useBranchStore(
    (s) => s.selectedBranchOfficeId
  );
  const branchOffices = useBranchStore((s) => s.branchOffices);

  if (!isAuthenticated) return 'unauthenticated';
  if (!currentBusinessId) return 'missing-business-id';
  if (!selectedBranchOfficeId) return 'branch-not-selected';

  if (branchOffices.length === 0) return 'ready';

  const branchesMatchTenant = branchOffices.every(
    (b) => !!b.businessId && b.businessId === currentBusinessId
  );
  if (!branchesMatchTenant) return 'branch-tenant-mismatch';

  if (
    selectedBranchOfficeId !== 'all' &&
    !branchOffices.some((b) => b.id === selectedBranchOfficeId)
  ) {
    return 'branch-not-in-list';
  }

  return 'ready';
}
